// Linked List — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as binarySearch.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import linkedListDrills from './linkedListDrills';
const drill = id => linkedListDrills.find(d => d.id === id);

const linkedList = {
  key: 'Linked List',
  tagline: 'No contiguous memory, no random access — just nodes that each know where the next one lives.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Linked Lists',
      type: 'text',
      body: [
        "An array stores its elements back-to-back in one continuous block of memory, which is exactly what makes arr[i] an instant O(1) lookup. A linked list gives that up entirely: each element (called a node) lives wherever the JVM happens to place it, and the only way to get from one node to the next is to follow an explicit pointer stored inside the node itself.",
        "That trade produces the opposite strengths and weaknesses of an array. Reading an arbitrary position becomes O(n) — you must walk from the head, one node at a time, since there's no address arithmetic that can jump straight there. But inserting or deleting a node, once you're already standing at the right spot, is O(1) — no shifting every following element down by one, the way an array insert requires.",
      ],
    },
    {
      id: 'why',
      title: 'Why Linked Lists?',
      type: 'text',
      body: [
        "Arrays are fixed in size and expensive to insert into the middle of. A linked list has neither problem: it grows one node at a time with no resizing step, and inserting or removing a node anywhere is just a couple of pointer rewrites, once you're positioned there.",
        "The cost is giving up random access and giving up a length field — a linked list doesn't know how many nodes it has without walking the whole thing (unless a count is maintained separately), and it can't jump to \"the middle\" the way arr[arr.length/2] can. This single fact is why the fast & slow pointer technique — walking one pointer at double speed — exists: it's the only way to find a linked list's middle, or detect whether it loops back on itself, without ever knowing its length upfront.",
        "This pattern group is really about pointer discipline: reversal, merging, and cycle detection are all just different ways of carefully rewiring next references without ever losing track of a node you still need.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Linked Lists Work — A Treasure Hunt With One-Way Clues',
      type: 'text',
      heading: 'Each Clue Only Reveals the Next One — Never the Whole Map',
      body: [
        "Picture a treasure hunt where every clue is hidden somewhere different, and each clue you find only tells you the location of the NEXT clue — never a full map, and never a way to jump ahead or go back. To reach the fifth clue, you have no choice but to physically visit the first four in order; there's no shortcut, the way flipping to page 5 of a book skips pages 1 through 4 instantly. That's exactly what walking a linked list feels like: curr = curr.next is the only way forward, and skipping ahead simply isn't possible.",
        "Now picture that same treasure hunt gone wrong: one of the clues, instead of pointing to a brand-new location, secretly points BACK to a clue you already visited earlier — meaning some unlucky hunter following the clues faithfully would loop forever, never reaching a final \"you found it\" spot. To detect this without a map (without knowing the hunt's total length upfront), send out two hunters at once from the start — one walking normally, one skipping every other clue. If the trail truly ends, the fast hunter simply arrives first. But if it loops, the fast hunter will eventually come back around and bump into the slow one from behind — proving the loop exists, without either of them ever needing to know how many clues there are in total. This is Floyd's cycle detection, and it's the single most important reason fast & slow pointers exist.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Node Chain',
      type: 'node-diagram',
      body: [
        "Unlike an array's tightly-packed boxes, a linked list is drawn as separate nodes connected by arrows — because that's literally what it is: each node is its own object, holding a value and a reference to whatever comes next. The chain ends at null, which is how Java represents \"there is nothing after this.\"",
        "The diagram below shows a small list with a head pointer. Nothing about this structure lets you jump to, say, the third node directly — the only way to reach it is to follow the arrows from the head, one hop at a time.",
      ],
      demo: {
        nodes: [12, 45, 7, 23],
        pointers: [{ label: 'head', index: 0, color: '#7F00FF' }],
        note: 'head.next.next reaches the third node (value 7) — there is no arr[2]-style shortcut; every hop must be taken explicitly.',
      },
    },
    {
      id: 'declare',
      title: 'The Node Class and Declaring a List in Java',
      type: 'code',
      code:
`// The building block of every linked list
class ListNode {
    int val;
    ListNode next;

    ListNode(int val) {
        this.val = val;
        this.next = null;
    }
    ListNode(int val, ListNode next) {
        this.val = val;
        this.next = next;
    }
}

// A list is just a reference to its first node
ListNode head = new ListNode(10, new ListNode(20, new ListNode(30)));`,
      note: 'There is no built-in LinkedList "array-like" syntax the way int[] works — a linked list in interview code is almost always this small hand-written ListNode class (Java\'s own java.util.LinkedList exists, but interview problems build the node structure explicitly so the pointer manipulation is visible).',
    },
    {
      id: 'traverse',
      title: 'Traversal and Insertion',
      type: 'code',
      code:
`// Traverse
ListNode curr = head;
while (curr != null) {
    System.out.println(curr.val);
    curr = curr.next;
}

// Insert a new node right after a given node
ListNode newNode = new ListNode(99);
newNode.next = given.next;
given.next = newNode;

// Insert at the very front (changes what "head" refers to)
ListNode newHead = new ListNode(0, head);
head = newHead;`,
      note: 'Order matters when inserting: newNode.next must be set BEFORE given.next is overwritten — reverse that order and the rest of the original list is permanently lost.',
    },
    {
      id: 'dummy',
      title: 'The Dummy Head Node Technique',
      type: 'code',
      code:
`ListNode dummy = new ListNode(0);
dummy.next = head;
ListNode prev = dummy;

// Now every real node, INCLUDING the original head, has a "prev" —
// no special-casing needed for "what if we're deleting the first node?"
while (prev.next != null) {
    if (shouldDelete(prev.next)) {
        prev.next = prev.next.next;
    } else {
        prev = prev.next;
    }
}
return dummy.next;   // the (possibly new) head`,
      note: 'Without a dummy node, deleting or modifying the head requires a separate special case ("if this is the head, do X; otherwise do Y"). A dummy node placed before the real head means every node — including the original head — always has a predecessor, so the exact same code handles every position uniformly.',
    },
    {
      id: 'fastslow',
      title: 'Fast & Slow Pointers, Applied to Lists',
      type: 'code',
      code:
`ListNode slow = head, fast = head;
while (fast != null && fast.next != null) {
    slow = slow.next;
    fast = fast.next.next;
}
// slow is now the middle node`,
      note: 'On an array, finding the middle is trivial: arr[arr.length / 2]. A linked list has no length field to read — this is genuinely the ONLY way to find the middle (or detect a cycle) in one pass without first walking the whole list just to count it.',
    },
    {
      id: 'reverse',
      title: 'Reversal: The Prev / Curr / Next Template',
      type: 'code',
      code:
`ListNode prev = null, curr = head;
while (curr != null) {
    ListNode next = curr.next;   // save it — about to overwrite curr.next
    curr.next = prev;             // reverse this node's pointer
    prev = curr;
    curr = next;
}
head = prev;   // prev ends up as the new head`,
      note: 'The single most common mistake here is forgetting to save next BEFORE overwriting curr.next — do that, and the rest of the original list becomes unreachable the instant curr.next is reassigned.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Overwriting curr.next before saving it',
          why: 'curr.next = prev discards the only reference to the rest of the original list — if next wasn\'t saved into a separate variable first, everything after curr is permanently lost, not just temporarily hidden.',
          fix: 'Always save ListNode next = curr.next as the very first line inside a reversal loop, before touching curr.next at all.',
        },
        {
          mistake: 'Forgetting to null-check before dereferencing .next',
          why: 'Calling fast.next.next when fast is already null (or fast.next is null) throws a NullPointerException — a very common crash in fast/slow pointer code.',
          fix: 'The loop condition must check both fast != null AND fast.next != null before ever touching fast.next.next.',
        },
        {
          mistake: 'Not using a dummy node when the head itself might be deleted or replaced',
          why: 'Without one, code ends up with a separate special case for "is this the head?" scattered throughout — easy to get subtly wrong or forget an edge case (an empty list, a one-node list).',
          fix: 'Introduce ListNode dummy = new ListNode(0, head) at the start, operate through dummy.next uniformly, and return dummy.next at the end.',
        },
        {
          mistake: 'Losing the head reference after reassigning it during traversal',
          why: 'Walking the list with head = head.next directly (instead of a separate curr variable) destroys the only reference back to the start of the list.',
          fix: 'Always traverse with a separate ListNode curr = head; curr = curr.next; — keep head itself untouched unless the problem explicitly asks for a new head.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Access by position (walk from head)', time: 'O(n)', note: 'No address arithmetic is possible — every node before it must be visited.' },
        { op: 'Insert / delete, given a reference to the spot', time: 'O(1)', note: 'Just a couple of pointer rewrites — no shifting, unlike an array.' },
        { op: 'Search for a value', time: 'O(n)', note: 'Must walk from the head; no way to jump toward a likely position.' },
        { op: 'Reverse / find middle / detect a cycle', time: 'O(n)', note: 'Each visits every node at most a constant number of times.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for a linked list when you see…',
      body: [
        'Frequent insertions or deletions in the middle of a sequence, where an array would need to shift every following element.',
        'A structure whose size isn\'t known upfront and changes constantly — no resizing step is ever needed.',
        'A problem that\'s explicitly phrased in terms of ListNode and .next — reversal, merging, cycle detection, reordering.',
        'A need for O(1) insertion at both ends combined with sequential access — this is exactly what a queue or deque built on a doubly linked list gives you.',
        'Avoid it when random access by index is common — an ArrayList or plain array will always beat a linked list there, since O(n) traversal to reach an arbitrary index is a real cost a linked list can\'t avoid.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('traverse-and-count-length'),
            drill('find-middle-node'),
            drill('reverse-linked-list-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('remove-duplicates-sorted-list'),
            drill('detect-cycle-drill'),
            drill('merge-two-sorted-lists-drill'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('reverse-linked-list-in-groups-of-k'),
            drill('remove-nth-from-end-drill'),
            drill('intersection-of-two-linked-lists'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with pointer rewiring, the dummy-head technique, and fast/slow pointers before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'No random access: reaching position i costs O(n), always — there is no arr[i]-style shortcut.',
        'Insert/delete at a known position is O(1) — just pointer rewrites, no shifting.',
        'Save next before overwriting curr.next during a reversal — this is the single most common mistake in the entire pattern.',
        'A dummy head node removes the need for special-casing "what if the head itself changes" — operate through dummy.next and return it at the end.',
        'Fast & slow pointers are the ONLY way to find a linked list\'s middle, or detect a cycle, without first walking the whole list to measure it.',
        'Always null-check both fast != null and fast.next != null before dereferencing fast.next.next.',
        'A linked list trades away random access to gain O(1) insertion/deletion at a known position — pick the one that matches what the problem actually does more often.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default linkedList;
